import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plane, Pin, Star, Loader2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { performRefresh } from '../../lib/refresh'
import { useImmigrationCountries, useImmigrationUserState } from '../../hooks/useImmigration'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import CountryDetailSheet from './components/CountryDetailSheet'
import { cn } from '../../lib/utils'

// Owner-only Immigration research surface.
// Phase 1: country grid + detail sheet with notes, status, pin.
// Phase 2 (later): per-country docs + milestones tracker.
// Phase 3 (later): AI assistant grounded in the user's profile.

export function ImmigrationPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { countries, loading: countriesLoading, refetch: refetchCountries } = useImmigrationCountries()
  const { stateBySlug, upsertState, refetch: refetchState } = useImmigrationUserState()
  const [selectedSlug, setSelectedSlug] = useState(null)

  const isAr = i18n.language === 'ar'

  React.useEffect(() => {
    if (!user) return
    if (!hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, navigate])

  const goBack = () => navigate('/')

  const handleRefresh = useCallback(
    () => performRefresh([refetchCountries, refetchState]),
    [refetchCountries, refetchState],
  )

  // Pinned first, then alphabetical (which is already the seed
  // display_order in the migration). Don't re-sort by status because
  // a country marked "not_interested" should still appear in the
  // research surface — just not pinned.
  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      const aPin = stateBySlug.get(a.slug)?.pinned ? 1 : 0
      const bPin = stateBySlug.get(b.slug)?.pinned ? 1 : 0
      if (aPin !== bPin) return bPin - aPin
      return a.display_order - b.display_order
    })
  }, [countries, stateBySlug])

  const selectedCountry = selectedSlug
    ? countries.find((c) => c.slug === selectedSlug)
    : null

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-4 content-fade pb-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              <Plane className="w-5 h-5 text-app-primary-500" />
              {t('immigration.title')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
              {t('immigration.subtitle')}
            </p>
          </div>
        </div>

        {/* Grid */}
        {countriesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
          </div>
        ) : sortedCountries.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
              {t('immigration.empty')}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sortedCountries.map((c) => (
              <CountryCard
                key={c.slug}
                country={c}
                state={stateBySlug.get(c.slug)}
                isAr={isAr}
                onTap={() => setSelectedSlug(c.slug)}
              />
            ))}
          </div>
        )}

        <CountryDetailSheet
          open={!!selectedCountry}
          country={selectedCountry}
          userState={stateBySlug.get(selectedSlug)}
          onClose={() => setSelectedSlug(null)}
          onUpdate={async (patch) => { await upsertState(patch); }}
        />
      </div>
    </PullToRefresh>
  )
}

function CountryCard({ country, state, isAr, onTap }) {
  const { t } = useTranslation()
  const name = isAr ? country.name_ar : country.name_en
  const pinned = state?.pinned
  const status = state?.status

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTap() }}
      className={cn(
        'cursor-pointer transition-colors',
        pinned ? 'border-app-primary-500/60' : 'hover:border-app-primary-500/30',
      )}
    >
      <CardContent className="p-3.5 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl">{country.flag_emoji}</span>
            <h2 className="text-base font-semibold text-foreground truncate" dir="auto">{name}</h2>
          </div>
          {pinned && <Pin className="w-3.5 h-3.5 text-app-primary-500 flex-shrink-0" />}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge label={t('immigration.tolerance')} score={country.tolerance_score} />
          <Badge label={t('immigration.stability')} score={country.stability_score} />
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 font-mono">
            {country.citizenship_years} {t('immigration.years')}
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground line-clamp-1" dir="auto">
          {(country.primary_languages || []).join(' · ')}
        </p>

        {status && (
          <span className={cn(
            'text-[10px] px-1.5 py-0.5 rounded inline-block',
            status === 'pursuing' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
            status === 'interested' && 'bg-app-primary-500/10 text-app-primary-500',
            status === 'researching' && 'bg-muted text-muted-foreground',
            status === 'not_interested' && 'bg-red-500/10 text-red-600',
          )}>
            {t(`immigration.status.${status}`)}
          </span>
        )}
      </CardContent>
    </Card>
  )
}

function Badge({ label, score }) {
  return (
    <span className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-muted text-muted-foreground" title={label}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'w-2.5 h-2.5',
            i < score ? 'fill-app-primary-500 text-app-primary-500' : 'text-muted-foreground/30',
          )}
        />
      ))}
    </span>
  )
}
